import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layers, Plus, Pencil, Trash2, Loader2, ChevronUp, ChevronDown, ArrowLeft, Link2 } from "lucide-react";

import { Header } from "../layout/Header";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { ShareLinkButton } from "../layout/ShareLinkButton";
import { TopicFormDialog, TopicFormValues } from "./TopicFormDialog";
import { PromptFormDialog, PromptFormValues } from "./PromptFormDialog";
import { AiTopic } from "./AiTopicsPage";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { CATEGORIES } from "../../lib/categories";
import { getStoredCurrentUser } from "../../lib/app-events";
import ApiService from "../../services/api-service";
import { showErrorToast, showInfoToast, showSuccessToast } from "../../lib/toast";
import { shareLink } from "../../lib/share";
import { PUBLIC_SITE_URL } from "../../constants/app";
import { AI_PROMPT_COMPLEXITY_META, normalizeAiPromptComplexity } from "../../constants/feed-meta";
import { timeAgo } from "../../lib/format";
import { CurrentUser } from "../../types/user";

// A neutral left accent, same for every complexity level - this page uses a
// single accent color throughout rather than a different hue per level.
const PROMPT_CARD_ACCENT = "border-l-[#fc991d]/60 dark:border-l-[#fc991d]/70";

// A single neutral badge style for every complexity level, local to this
// page (the shared AI_PROMPT_COMPLEXITY_META badgeClass is red/amber/green
// per level, which is used by the main feed but would reintroduce the same
// rainbow-of-colors look here).
const COMPLEXITY_BADGE_CLASS =
  "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";

interface TopicPrompt {
  id: number;
  topic_id: number;
  content: string;
  complexity: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  username: string | null;
  up_count: number;
  down_count: number;
  my_vote: "up" | "down" | null;
}

const categoryOptions = CATEGORIES.slice(1).map((cat: any) => ({ id: cat.id, name: cat.name }));

function VoteControl({
  prompt,
  disabled,
  onVote,
}: {
  prompt: TopicPrompt;
  disabled?: boolean;
  onVote: (voteType: "up" | "down") => void;
}) {
  const score = prompt.up_count - prompt.down_count;
  return (
    <div className="flex flex-col items-center gap-1 shrink-0 w-12 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 py-1.5">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onVote("up")}
        aria-label="Upvote"
        aria-pressed={prompt.my_vote === "up"}
        className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors disabled:opacity-50 ${
          prompt.my_vote === "up"
            ? "text-[#fc991d] dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10"
            : "text-slate-400 hover:text-[#fc991d] hover:bg-orange-50 dark:hover:text-orange-400 dark:hover:bg-orange-500/10"
        }`}
      >
        <ChevronUp className="w-5 h-5" />
      </button>
      <span className="text-sm font-bold text-slate-700 dark:text-slate-200 tabular-nums">{score}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onVote("down")}
        aria-label="Downvote"
        aria-pressed={prompt.my_vote === "down"}
        className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors disabled:opacity-50 ${
          prompt.my_vote === "down"
            ? "text-[#fc991d] dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10"
            : "text-slate-400 hover:text-[#fc991d] hover:bg-orange-50 dark:hover:text-orange-400 dark:hover:bg-orange-500/10"
        }`}
      >
        <ChevronDown className="w-5 h-5" />
      </button>
    </div>
  );
}

// /ai-topics/:topicId - one topic's prompts: add, edit, delete, and vote.
export function AiTopicDetailPage() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [topic, setTopic] = useState<AiTopic | null>(null);
  const [prompts, setPrompts] = useState<TopicPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [votingId, setVotingId] = useState<number | null>(null);

  const [topicFormOpen, setTopicFormOpen] = useState(false);
  const [isSavingTopic, setIsSavingTopic] = useState(false);
  const [deletingTopic, setDeletingTopic] = useState(false);
  const [isDeletingTopic, setIsDeletingTopic] = useState(false);

  const [promptFormOpen, setPromptFormOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<TopicPrompt | null>(null);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [deletingPrompt, setDeletingPrompt] = useState<TopicPrompt | null>(null);
  const [isDeletingPrompt, setIsDeletingPrompt] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleUserChange = (event: Event) => {
      setCurrentUser(event instanceof CustomEvent ? event.detail ?? null : getStoredCurrentUser());
    };
    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => window.removeEventListener("inspirelens:userchange", handleUserChange);
  }, []);

  useEffect(() => {
    if (!topicId) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setNotFound(false);

    ApiService.getAiTopic(topicId)
      .then((res: { topic?: AiTopic; prompts?: TopicPrompt[] }) => {
        if (cancelled) return;
        if (!res.topic) {
          setNotFound(true);
          return;
        }
        setTopic(res.topic);
        setPrompts(res.prompts || []);
        document.title = `${res.topic.title} · AI Topics · The Deepam`;
      })
      .catch((err: Error) => {
        if (cancelled) return;
        if (err.message === "Topic not found") setNotFound(true);
        else setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [topicId, retryToken]);

  // A per-prompt Share link (see handleSharePrompt) points at #prompt-<id> -
  // once the prompts are in, scroll that one into view so the link actually
  // lands on the prompt it was shared for.
  useEffect(() => {
    if (loading || typeof window === "undefined" || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [loading, prompts]);

  const isTopicOwner = !!currentUser?.id && !!topic && Number(currentUser.id) === Number(topic.user_id);

  // Prompts don't have their own page, so "Share" copies a link to this
  // topic page anchored at this specific prompt (#prompt-<id>), which the
  // scroll effect above then scrolls to on load.
  const handleSharePrompt = async (prompt: TopicPrompt) => {
    const url = `${PUBLIC_SITE_URL}/ai-topics/${topicId}#prompt-${prompt.id}`;
    const outcome = await shareLink({ title: `${topic?.title || "AI Topic"} · The Deepam`, url });
    if (outcome === "copied") {
      showSuccessToast("Link to this prompt copied to clipboard!");
    } else if (outcome === "failed") {
      showInfoToast(`Unable to copy automatically. Link: ${url}`);
    }
  };

  const handleVote = async (prompt: TopicPrompt, voteType: "up" | "down") => {
    if (!currentUser) {
      onOpenAuth("login");
      return;
    }
    if (!topicId || votingId === prompt.id) return;
    setVotingId(prompt.id);
    // Optimistic update so the click feels instant; reconciled below.
    const previous = prompt;
    setPrompts((prev) =>
      prev.map((p) => {
        if (p.id !== prompt.id) return p;
        let up = p.up_count;
        let down = p.down_count;
        if (p.my_vote === "up") up -= 1;
        if (p.my_vote === "down") down -= 1;
        const nextVote = p.my_vote === voteType ? null : voteType;
        if (nextVote === "up") up += 1;
        if (nextVote === "down") down += 1;
        return { ...p, up_count: up, down_count: down, my_vote: nextVote };
      })
    );

    try {
      const res = await ApiService.voteAiTopicPrompt(topicId, prompt.id, voteType);
      setPrompts((prev) =>
        prev.map((p) => (p.id === prompt.id ? { ...p, up_count: res.up_count, down_count: res.down_count, my_vote: res.my_vote } : p))
      );
    } catch (err) {
      setPrompts((prev) => prev.map((p) => (p.id === prompt.id ? previous : p)));
      showErrorToast("Unable to record your vote. Please try again.");
    } finally {
      setVotingId(null);
    }
  };

  const handleEditTopicSubmit = async (values: TopicFormValues) => {
    if (!topic) return;
    setIsSavingTopic(true);
    try {
      await ApiService.updateAiTopic(topic.id, values);
      setTopic((prev) =>
        prev
          ? {
              ...prev,
              title: values.title,
              description: values.description,
              category_id: Number(values.category),
              category_name: categoryOptions.find((c) => String(c.id) === values.category)?.name ?? prev.category_name,
            }
          : prev
      );
      showSuccessToast("Topic updated successfully!");
      setTopicFormOpen(false);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to update topic. Please try again.");
    } finally {
      setIsSavingTopic(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!topic) return;
    setIsDeletingTopic(true);
    try {
      await ApiService.deleteAiTopic(topic.id);
      showSuccessToast("Topic deleted successfully!");
      navigate("/ai-topics");
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to delete topic. Please try again.");
      setIsDeletingTopic(false);
    }
  };

  const openAddPrompt = () => {
    if (!currentUser) {
      onOpenAuth("login");
      return;
    }
    setEditingPrompt(null);
    setPromptFormOpen(true);
  };

  const openEditPrompt = (prompt: TopicPrompt) => {
    setEditingPrompt(prompt);
    setPromptFormOpen(true);
  };

  const handlePromptSubmit = async (values: PromptFormValues) => {
    if (!topicId) return;
    setIsSavingPrompt(true);
    try {
      if (editingPrompt) {
        await ApiService.updateAiTopicPrompt(topicId, editingPrompt.id, values);
        setPrompts((prev) => prev.map((p) => (p.id === editingPrompt.id ? { ...p, content: values.content, complexity: values.complexity } : p)));
        showSuccessToast("Prompt updated successfully!");
      } else {
        const res = await ApiService.addAiTopicPrompt(topicId, values);
        if (res?.prompt) setPrompts((prev) => [...prev, res.prompt]);
        showSuccessToast("Prompt added successfully!");
      }
      setPromptFormOpen(false);
      setEditingPrompt(null);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to save prompt. Please try again.");
    } finally {
      setIsSavingPrompt(false);
    }
  };

  const handleDeletePrompt = async () => {
    if (!topicId || !deletingPrompt) return;
    setIsDeletingPrompt(true);
    try {
      await ApiService.deleteAiTopicPrompt(topicId, deletingPrompt.id);
      setPrompts((prev) => prev.filter((p) => p.id !== deletingPrompt.id));
      showSuccessToast("Prompt deleted successfully!");
      setDeletingPrompt(null);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to delete prompt. Please try again.");
    } finally {
      setIsDeletingPrompt(false);
    }
  };

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />

      <Button variant="ghost" size="sm" onClick={() => navigate("/ai-topics")} className="mt-6 -ml-2 text-slate-600 dark:text-slate-400">
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        All AI Topics
      </Button>

      {notFound ? (
        <div className="mt-6 text-center py-16 space-y-2">
          <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Topic not found</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">It may have been deleted.</p>
        </div>
      ) : error ? (
        <div className="mt-6 text-center py-16 space-y-3">
          <p className="text-lg font-medium text-slate-700 dark:text-slate-200">We couldn't load this topic.</p>
          <Button onClick={() => setRetryToken((n) => n + 1)} variant="outline" className="mt-2">
            Try Again
          </Button>
        </div>
      ) : loading || !topic ? (
        <div className="mt-6 space-y-4">
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-24 w-full rounded-2xl" />
        </div>
      ) : (
        <>
          <section className="relative mt-4 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
              <div className="absolute -top-16 -right-12 h-44 w-44 rounded-full bg-gradient-to-br from-[#fc991d]/20 via-orange-300/10 to-transparent blur-3xl" />
            </div>

            <div className="relative p-5 sm:p-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex items-start gap-3">
                <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#fc991d] to-orange-500 text-white shadow-sm">
                  <Layers className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 break-words">{topic.title}</h1>
                  {topic.description && <p className="text-slate-600 dark:text-slate-400 mt-1.5 text-sm">{topic.description}</p>}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-xs text-slate-500 dark:text-slate-400">
                    {topic.category_name && (
                      <span className="rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 px-2.5 py-1 font-medium">
                        {topic.category_name}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-slate-600 dark:bg-slate-500 grid place-items-center text-white font-medium text-[9px] shrink-0">
                        {(topic.username || "?").slice(0, 2).toUpperCase()}
                      </span>
                      {topic.username || "Unknown"}
                    </span>
                    <span>{timeAgo(topic.created_at)} ago</span>
                    <span>
                      {prompts.length} {prompts.length === 1 ? "prompt" : "prompts"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <ShareLinkButton title={topic.title} />
                {isTopicOwner && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setTopicFormOpen(true)}>
                      <Pencil className="w-4 h-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletingTopic(true)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 border-red-200 dark:border-red-900"
                    >
                      <Trash2 className="w-4 h-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Delete</span>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </section>

          <div className="mt-6 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
              {prompts.length} {prompts.length === 1 ? "Prompt" : "Prompts"}
            </h2>
            <Button
              onClick={openAddPrompt}
              size="sm"
              className="bg-[#fc991d] hover:bg-[#e8890a] text-white shadow-sm"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add Prompt
            </Button>
          </div>

          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {prompts.length === 0 ? (
              <div className="lg:col-span-2 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 py-12 text-center space-y-2">
                <p className="text-slate-600 dark:text-slate-300 text-sm font-medium">No prompts in this topic yet.</p>
                <Button onClick={openAddPrompt} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add the first one
                </Button>
              </div>
            ) : (
              prompts.map((prompt) => {
                const isPromptOwner = !!currentUser?.id && Number(currentUser.id) === Number(prompt.user_id);
                const level = normalizeAiPromptComplexity(prompt.complexity);
                const levelMeta = AI_PROMPT_COMPLEXITY_META[level];
                return (
                  <div
                    key={prompt.id}
                    id={`prompt-${prompt.id}`}
                    className={`group rounded-2xl border border-slate-200 dark:border-slate-800 border-l-4 ${PROMPT_CARD_ACCENT} bg-white dark:bg-slate-900 p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md scroll-mt-20`}
                  >
                    <div className="flex items-start gap-4">
                      <VoteControl prompt={prompt} disabled={votingId === prompt.id} onVote={(voteType) => handleVote(prompt, voteType)} />
                      <div className="min-w-0 flex-1 space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <Badge variant="outline" className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${COMPLEXITY_BADGE_CLASS}`}>
                            {levelMeta.label}
                          </Badge>
                          <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                            <span className="w-5 h-5 rounded-full bg-slate-600 dark:bg-slate-500 grid place-items-center text-white font-medium text-[9px] shrink-0">
                              {(prompt.username || "?").slice(0, 2).toUpperCase()}
                            </span>
                            <span className="font-medium text-slate-600 dark:text-slate-300">{prompt.username || "Unknown"}</span>
                            <span className="text-slate-300 dark:text-slate-600">·</span>
                            {timeAgo(prompt.created_at)} ago
                          </span>
                        </div>

                        <p className="text-[15px] leading-relaxed text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
                          {prompt.content}
                        </p>

                        {/* Same icon+label action-row idea as the main feed's
                            cards, styled as pill buttons (rather than bare
                            text links) so they read as clearly tappable -
                            scoped here to Share plus Edit/Delete for the
                            prompt's owner. */}
                        <div className="flex items-center gap-1.5 pt-1 -ml-2.5">
                          <button
                            type="button"
                            onClick={() => handleSharePrompt(prompt)}
                            aria-label="Copy a link to this prompt"
                            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                            Share
                          </button>

                          {isPromptOwner && (
                            <>
                              <button
                                type="button"
                                onClick={() => openEditPrompt(prompt)}
                                aria-label="Edit prompt"
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeletingPrompt(prompt)}
                                aria-label="Delete prompt"
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20 dark:hover:text-red-300 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <TopicFormDialog
            open={topicFormOpen}
            mode="edit"
            categories={categoryOptions}
            initial={{ title: topic.title, description: topic.description || "", category: String(topic.category_id) }}
            isLoading={isSavingTopic}
            onClose={() => !isSavingTopic && setTopicFormOpen(false)}
            onSubmit={handleEditTopicSubmit}
          />

          <Dialog open={deletingTopic} onOpenChange={(value: boolean) => !value && !isDeletingTopic && setDeletingTopic(false)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Delete Topic</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{topic.title}"? This also removes every prompt inside it. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="ghost" onClick={() => setDeletingTopic(false)} disabled={isDeletingTopic}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDeleteTopic} disabled={isDeletingTopic} className="bg-red-600 hover:bg-red-700 text-white">
                  {isDeletingTopic ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Topic
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      <PromptFormDialog
        open={promptFormOpen}
        mode={editingPrompt ? "edit" : "create"}
        initial={editingPrompt ? { content: editingPrompt.content, complexity: normalizeAiPromptComplexity(editingPrompt.complexity) } : undefined}
        isLoading={isSavingPrompt}
        onClose={() => {
          if (!isSavingPrompt) {
            setPromptFormOpen(false);
            setEditingPrompt(null);
          }
        }}
        onSubmit={handlePromptSubmit}
      />

      <Dialog open={!!deletingPrompt} onOpenChange={(value: boolean) => !value && !isDeletingPrompt && setDeletingPrompt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Prompt</DialogTitle>
            <DialogDescription>Are you sure you want to delete this prompt? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={() => setDeletingPrompt(null)} disabled={isDeletingPrompt}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePrompt} disabled={isDeletingPrompt} className="bg-red-600 hover:bg-red-700 text-white">
              {isDeletingPrompt ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Prompt
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AiTopicDetailPage;
